import { createGlobalStyle } from 'styled-components';
import { formatTokensAsCssVars } from '@statsbomb/kitbag-themes';

/**
 * The active theme's colours, written as CSS custom property declarations.
 *
 * Kept separate from the component below so it can be tested without a DOM.
 */
export const colourCustomProperties = (theme) =>
  formatTokensAsCssVars({ tokens: theme.colours, prefix: 'colours' });

/**
 * The active theme's border radii, written the same way.
 *
 * The two themes disagree about border radius, so a stylesheet that writes one
 * raw would keep the old shape at the switch while every component changed.
 */
export const borderRadiusCustomProperties = (theme) =>
  formatTokensAsCssVars({ tokens: theme.borderRadius, prefix: 'borderRadius' });

/**
 * The active theme's durations and easings, written the same way.
 *
 * `index.scss` gives the scrollbar thumb a colour transition, so it needs the
 * same duration and easing a component would name. ADR-0003 publishes a group
 * once a stylesheet needs it.
 */
export const animationCustomProperties = (theme) =>
  formatTokensAsCssVars({ tokens: theme.animation, prefix: 'animation' });

/**
 * Publishes the active theme's colours, border radii and animation tokens as CSS
 * custom properties on `:root`.
 *
 * SCSS is compiled before the app runs, so at that moment no theme has been
 * chosen and a stylesheet cannot read a token. A custom property is a named slot
 * the browser fills in while the app runs, so a stylesheet can name the token
 * without ever learning its value. See ADR-0003.
 *
 * Mounted inside the theme provider, so the properties follow the dark and light
 * theme the app is actually using.
 */
export const ThemeCustomProperties = createGlobalStyle`
  :root {
    ${({ theme }) => colourCustomProperties(theme)}
    ${({ theme }) => borderRadiusCustomProperties(theme)}
    ${({ theme }) => animationCustomProperties(theme)}
  }
`;
